import { useEffect, useRef, useState } from 'react';
import { formatPercent, formatRial } from '@kia/shared';
import { api } from '../api/client';
import { postPrice } from '../api/priceWrite';
import { Button, Card, Chip, Field, Input, Modal, NumberInput, useConfirm, useToast } from '../ui';

/**
 * پشتوانهٔ قیمت — the price-proof gate (soft). Each MAJOR group's book price must be backed by a supplier
 * offer / proforma (P/I), AI-read, ≤ `days` old. This lists the major groups with proven / stale / missing
 * status and lets the operator upload a proof (the AI reads its price) that then backs — and adopts — the
 * group price. Complements the price-drift monitor (book-vs-paid) with book-vs-proof-freshness.
 */
interface ProofRow {
  guKey: string;
  group: string;
  unit: string;
  costShare: number;
  bookPrice: number | null;
  proof: {
    price: number;
    kind: string;
    supplierName: string;
    issuedAt: string;
    ageDays: number;
    docId: number | null;
  } | null;
  status: 'proven' | 'stale' | 'missing';
  priceMismatch: boolean;
}
interface ProofStatus {
  asOf: string;
  days: number;
  minShare: number;
  rows: ProofRow[];
  proven: number;
  stale: number;
  missing: number;
  mismatch: number;
  ok: boolean;
}
interface Extraction {
  price: number;
  supplierName: string;
  issuedAt: string;
  kind: 'supplier_offer' | 'proforma';
  materialHint: string;
}

const today = () => new Date().toISOString().slice(0, 10);

export function PriceProofCard({ canEdit, onSaved }: { canEdit: boolean; onSaved?: () => void }) {
  const [st, setSt] = useState<ProofStatus | null>(null);
  const [row, setRow] = useState<ProofRow | null>(null); // the group whose proof is being uploaded

  function load() {
    void api<ProofStatus>('/api/prices/proof-status').then((r) => {
      if (r.ok) setSt(r.body as ProofStatus);
    });
  }
  useEffect(load, []);

  if (!st) return null;

  // A proof that is fresh BUT disagrees with the book price is not a green «دارای سند ✓» — the book is likely
  // wrong. Show a distinct «مغایر با کتاب» so the trust signal never contradicts the (≠ کتاب) note beside it.
  const chip = (r: ProofRow) =>
    r.status === 'proven' ? (
      r.priceMismatch ? (
        <Chip tone="warn">مغایر با کتاب</Chip>
      ) : (
        <Chip tone="success">دارای سند ✓</Chip>
      )
    ) : r.status === 'stale' ? (
      <Chip tone="warn">سند منقضی</Chip>
    ) : (
      <Chip tone="danger">بدون سند</Chip>
    );

  return (
    <Card
      title={
        <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
          پشتوانهٔ قیمت گروه‌های اصلی
          <Chip tone={st.ok ? 'success' : 'warn'}>
            {st.ok
              ? 'همه به‌روز ✓'
              : `${st.rows
                  .filter((r) => r.status !== 'proven' || r.priceMismatch)
                  .length.toLocaleString('fa-IR')} نیازمند رسیدگی`}
          </Chip>
        </span>
      }
    >
      <p style={{ margin: '0 0 10px', color: 'var(--ink-2)', lineHeight: 1.9 }}>
        قیمتِ گروه‌های اصلی (سهم بالای هزینه) باید با یک «پیش‌فاکتور تأمین‌کننده» یا «پیش‌فاکتور
        فروش (P/I)» پشتیبانی شود که حداکثر {st.days.toLocaleString('fa-IR')} روز از آن گذشته باشد.
        سند را بارگذاری کنید؛ هوش مصنوعی قیمت را از روی آن می‌خواند و همان قیمت مبنای گروه می‌شود.
        اگر سندی قدیمی یا غایب بماند، هر دو هفته یک‌بار یادآوری می‌شود.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>گروه</th>
              <th>سهم بها</th>
              <th>قیمت کتاب</th>
              <th>سند</th>
              <th>وضعیت</th>
              {canEdit ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {st.rows.map((r) => (
              <tr key={r.guKey}>
                <td>
                  {r.group}
                  {r.unit ? <span style={{ color: 'var(--ink-3)' }}> / {r.unit}</span> : null}
                </td>
                <td className="num">{formatPercent(r.costShare)}</td>
                <td className="num">{r.bookPrice != null ? formatRial(r.bookPrice) : '—'}</td>
                <td style={{ color: 'var(--ink-2)', fontSize: '.9em' }}>
                  {r.proof ? (
                    <>
                      {formatRial(r.proof.price)}
                      {r.priceMismatch ? (
                        <span style={{ color: 'var(--amber)' }}> (≠ کتاب)</span>
                      ) : null}
                      <br />
                      {r.proof.kind === 'proforma' ? 'P/I' : 'پیش‌فاکتور'} — {r.proof.issuedAt} (
                      {r.proof.ageDays.toLocaleString('fa-IR')} روز)
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{chip(r)}</td>
                {canEdit ? (
                  <td>
                    <Button variant="secondary" onClick={() => setRow(r)}>
                      بارگذاری سند
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {row ? (
        <ProofUploadModal
          row={row}
          onClose={() => setRow(null)}
          onSaved={() => {
            setRow(null);
            load();
            onSaved?.();
          }}
        />
      ) : null}
    </Card>
  );
}

function ProofUploadModal({
  row,
  onClose,
  onSaved,
}: {
  row: ProofRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const fileRef = useRef<{ filename: string; mime: string; dataBase64: string } | null>(null);
  const [fileName, setFileName] = useState('');
  const [price, setPrice] = useState(row.bookPrice ?? 0);
  const [supplierName, setSupplierName] = useState('');
  const [issuedAt, setIssuedAt] = useState(today());
  const [kind, setKind] = useState<'supplier_offer' | 'proforma'>('supplier_offer');
  const [aiUsed, setAiUsed] = useState(false);
  // GW-10: the server-issued token proving the price was AI-read from THIS document; sent back on save so
  // the server can trust the «AI-extracted» provenance instead of a forgeable flag.
  const [aiProofToken, setAiProofToken] = useState('');
  const [busy, setBusy] = useState<'read' | 'save' | null>(null);

  // Price-sanity guard (A3b): this proof price is about to BECOME the group's book price. A read/entered
  // value materially off the current book price is most often a mis-read (AI or human) — surface it before
  // it silently skews the group's COGS. Soft (non-blocking); the operator confirms with eyes open.
  const bookDeltaPct =
    row.bookPrice && row.bookPrice > 0 && price > 0
      ? (price - row.bookPrice) / row.bookPrice
      : null;
  const priceOff = bookDeltaPct != null && Math.abs(bookDeltaPct) >= 0.25;

  function onFile(f: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result);
      fileRef.current = { filename: f.name, mime: f.type, dataBase64: res.split(',')[1] ?? '' };
      setFileName(f.name);
    };
    reader.readAsDataURL(f);
  }

  async function readWithAi() {
    if (!fileRef.current) return toast('ابتدا سند را انتخاب کنید', 'error');
    setBusy('read');
    const r = await api<{ extraction?: Extraction; reply?: string; aiProofToken?: string }>(
      '/api/ai/extract-proof',
      { method: 'POST', body: JSON.stringify({ proof: fileRef.current }) },
    );
    setBusy(null);
    if (!r.ok)
      return toast((r.body as { message?: string }).message ?? 'خطا در خواندن سند', 'error');
    const b = r.body as { extraction?: Extraction; reply?: string; aiProofToken?: string };
    if (b.extraction) {
      setPrice(b.extraction.price);
      if (b.extraction.supplierName) setSupplierName(b.extraction.supplierName);
      if (b.extraction.issuedAt) setIssuedAt(b.extraction.issuedAt);
      setKind(b.extraction.kind);
      setAiUsed(true);
      setAiProofToken(b.aiProofToken ?? '');
      toast('قیمت از روی سند خوانده شد ✓');
    } else {
      toast(b.reply ?? 'قیمت خوانده نشد — دستی وارد کنید', 'error');
    }
  }

  async function save() {
    if (!(price > 0)) return toast('قیمت باید بزرگ‌تر از صفر باشد', 'error');
    setBusy('save');
    const r = await postPrice(
      '/api/prices/proof',
      {
        guKey: row.guKey,
        price,
        kind,
        supplierName,
        issuedAt,
        aiExtracted: aiUsed, // ignored by the server (provenance now derived from the token) — kept for clarity
        ...(aiProofToken ? { aiProofToken } : {}),
        ...(fileRef.current ? { proof: fileRef.current } : {}),
      },
      confirm,
    );
    setBusy(null);
    if (!r.ok) return toast((r.body as { message?: string }).message ?? 'خطا در ثبت', 'error');
    toast('پشتوانهٔ قیمت ثبت شد ✓');
    onSaved();
  }

  return (
    <Modal
      title={`بارگذاری سند قیمت — ${row.group}${row.unit ? ` / ${row.unit}` : ''}`}
      onClose={onClose}
      actions={
        <>
          <Button onClick={() => void save()} loading={busy === 'save'} disabled={!(price > 0)}>
            ثبت پشتوانه
          </Button>
          <Button variant="secondary" onClick={onClose}>
            بستن
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="سند (پیش‌فاکتور تأمین‌کننده یا P/I — تصویر یا PDF)">
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          />
        </Field>
        {fileName ? (
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <Button variant="secondary" onClick={() => void readWithAi()} loading={busy === 'read'}>
              خواندن با هوش مصنوعی
            </Button>
            <span style={{ color: 'var(--ink-3)' }}>{fileName}</span>
          </div>
        ) : null}

        <div className="form-grid">
          <Field label="نوع سند">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as 'supplier_offer' | 'proforma')}
            >
              <option value="supplier_offer">پیش‌فاکتور تأمین‌کننده</option>
              <option value="proforma">پیش‌فاکتور فروش (P/I)</option>
            </select>
          </Field>
          <Field label="قیمت روی سند (﷼)">
            <NumberInput value={price} onValue={setPrice} />
          </Field>
          <Field label="تأمین‌کننده">
            <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
          </Field>
          <Field label="تاریخ سند">
            <Input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
          </Field>
        </div>
        {priceOff ? (
          <div className="banner banner-warn" style={{ marginTop: 0 }}>
            ⚠ این قیمت {formatPercent(Math.abs(bookDeltaPct!), 0)}{' '}
            {bookDeltaPct! > 0 ? 'بالاتر' : 'پایین‌تر'} از قیمت کتاب فعلی (
            {formatRial(row.bookPrice!)}) است — پیش از ثبت بررسی کنید که درست خوانده شده باشد.
          </div>
        ) : null}
        <p style={{ color: 'var(--ink-3)', margin: 0 }}>
          با ثبت، این قیمت به‌عنوان قیمت مبنای گروه ثبت می‌شود و سند به‌عنوان پشتوانه نگهداری
          می‌گردد.
        </p>
      </div>
    </Modal>
  );
}
